import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

/**
 * What KIND of executor a claimed dispatch is: a container, a GitHub Actions
 * run, a human wait, a child invocation, a deterministic action, or an
 * evaluator.
 *
 * @since 1.0.0
 */
export const LoopExecutorKind = Schema.Literal(
  "container",
  "actions_run",
  "human",
  "child",
  "action",
  "evaluator",
);
export type LoopExecutorKind = Schema.Schema.Type<typeof LoopExecutorKind>;

/**
 * The three-valued liveness a presence probe reports (loop model §3).
 *
 * @since 1.0.0
 */
export const LoopPresence = Schema.Literal("running", "absent", "unknown");
export type LoopPresence = Schema.Schema.Type<typeof LoopPresence>;

/**
 * The concluded exit as a structurally-opaque jsonb record.
 *
 * The real schema is `@fm/cluster`'s `LoopExitEncoded` — the JSON-encoded form
 * of `Schema.Exit`. `@fm/db` owns the COLUMN, never the grammar, and never
 * imports the cluster; a reader that needs the typed exit decodes it there.
 *
 * @since 1.0.0
 */
export const LoopExitEncoded = Schema.Record({ key: Schema.String, value: Schema.Unknown });
export type LoopExitEncoded = Schema.Schema.Type<typeof LoopExitEncoded>;

/**
 * Loop executor model — the executor ledger, one row per claimed dispatch
 * (OL-203).
 *
 * `customerId`, `invocationId` and `externalRef` are plain strings with no
 * foreign key — cross-schema / evidence-preserving, by id only.
 *
 * @since 1.0.0
 */
export class LoopExecutor extends Model.Class<LoopExecutor>("LoopExecutor")({
  id: Model.Generated(Schema.String),

  customerId: Schema.String,

  invocationId: Schema.String,
  node: Schema.String,
  claim: Schema.String,

  kind: LoopExecutorKind,

  loopRef: Schema.NullOr(Schema.String),
  harness: Schema.NullOr(Schema.String),

  deferredToken: Schema.NullOr(Schema.String),
  callbackTokenHash: Schema.NullOr(Schema.String),

  dispatchedAt: Schema.NullOr(Schema.DateTimeUtc),
  ackedAt: Schema.NullOr(Schema.DateTimeUtc),
  lastProbeAt: Schema.NullOr(Schema.DateTimeUtc),
  lastHeartbeatAt: Schema.NullOr(Schema.DateTimeUtc),
  concludedAt: Schema.NullOr(Schema.DateTimeUtc),
  killTerminalAt: Schema.NullOr(Schema.DateTimeUtc),

  externalRef: Schema.NullOr(Schema.String),
  runnerId: Schema.NullOr(Schema.String),
  lastPresence: Schema.NullOr(LoopPresence),

  exit: Schema.NullOr(LoopExitEncoded),
  killStep: Schema.Number,

  detail: Schema.NullOr(Schema.Record({ key: Schema.String, value: Schema.Unknown })),

  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
}) {}
